import { Box, Popper, Typography } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Divider } from "rsuite";
import { useAppStore } from "../../../../../../../hooks/useStores.tsx";
import { formatScientific } from "../../../../../../../utils";
import { TgModel } from "../../../../../Models/store/tg-model-store.tsx";
import styles from "./TrainingParametersCard.module.scss";

interface TrainingParametersCardProps {
	model: TgModel;
	anchorEl: HTMLElement;
	onClose: () => void;
}

const TrainingParametersCard = ({ model, anchorEl, onClose }: TrainingParametersCardProps) => {
	const { t } = useTranslation("tg", { keyPrefix: "models.dashboard" });
	const { t: t2 } = useTranslation("tg", { keyPrefix: "models.parametersLrScheduler" });
	const { tgStore } = useAppStore();
	const { tgProjectStore } = tgStore;

	if (!model) return null;
	const dataset = tgProjectStore.paginatedDatasets.items.find((d) => d.id === model?.dataset_id);

	useEffect(() => {
		if (!anchorEl) return;
		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (entry && !entry.isIntersecting) {
					onClose();
				}
			},
			{ threshold: 0 },
		);
		observer.observe(anchorEl);
		return () => observer.disconnect();
	}, [anchorEl, onClose]);

	return (
		<Popper
			open={Boolean(anchorEl)}
			anchorEl={anchorEl}
			placement="left-start"
			modifiers={[
				{ name: "flip", options: { fallbackPlacements: ["right-start", "bottom-start", "top-start"] } },
				{ name: "preventOverflow", options: { altAxis: true, tether: true, padding: 8 } },
				{ name: "offset", options: { offset: [0, 8] } },
			]}
			sx={{ zIndex: 1300 }}
		>
			<ClickAwayListener
				onClickAway={(event) => {
					const target = event.target as Node | null;
					if (anchorEl && target && anchorEl.contains(target)) return;
					onClose();
				}}
			>
				<Box className={styles.trainingParametersCard} onScroll={(e) => e.stopPropagation()}>
					<Box className={styles.cardHeader}>
						<Typography className={styles.parametersCardTitle}>{t("parametersCard.header")}</Typography>
						<Box className={styles.closeButton} onClick={onClose}>
							✕
						</Box>
					</Box>
					<Divider className={styles.divider} />
					<Box className={styles.cardContent}>
						<Box className={styles.parameterRow}>
							<Typography className={styles.parameterLabel}>
								{t("parametersCard.dataset_title")}
							</Typography>
							<Typography className={styles.parameterValue}>{dataset?.name}</Typography>
						</Box>
						<Box className={styles.parameterRow}>
							<Typography className={styles.parameterLabel}>
								{t("parametersCard.images_count")}
							</Typography>
							<Typography className={styles.parameterValue}>{dataset?.images_count}</Typography>
						</Box>
						<Box className={`${styles.parameterRow} ${styles.topBorder}`}>
							<Typography className={styles.parameterLabel}>{t("parametersCard.base_model")}</Typography>
							<Typography className={styles.parameterValue}>
								{t(`table.training_version.${model.training_version}`)}
							</Typography>
						</Box>
						<Box className={styles.parameterSection}>
							<Typography className={styles.sectionTitle}>
								{t("parametersCard.parameters_title")}
							</Typography>
							<Box className={styles.parameterRow}>
								<Typography className={`${styles.parameterLabel} ${styles.lightBold}`}>
									{t("parametersCard.rank")}
								</Typography>
								<Typography className={styles.parameterValue}>
									{model.training_parameters?.rank}
								</Typography>
							</Box>
							<Box className={styles.parameterRow}>
								<Typography className={`${styles.parameterLabel} ${styles.lightBold}`}>
									{t("parametersCard.learning_rate")}
								</Typography>
								<Typography className={styles.parameterValue}>
									{formatScientific(model.training_parameters?.learning_rate as number)}
								</Typography>
							</Box>
							<Box className={styles.parameterRow}>
								<Typography className={`${styles.parameterLabel} ${styles.lightBold}`}>
									{t("parametersCard.lr_scheduler")}
								</Typography>
								<Typography className={styles.parameterValue}>
									{model.training_parameters?.lr_scheduler
										? t2(model.training_parameters.lr_scheduler as any)
										: ""}
								</Typography>
							</Box>
							<Box className={styles.parameterRow}>
								<Typography className={`${styles.parameterLabel} ${styles.lightBold}`}>
									{t("parametersCard.total_training_steps")}
								</Typography>
								<Typography className={styles.parameterValue}>
									{model.training_parameters?.total_training_steps ?? ""}
								</Typography>
							</Box>
						</Box>
					</Box>
				</Box>
			</ClickAwayListener>
		</Popper>
	);
};

export default TrainingParametersCard;
